import { useEffect, useRef, useState } from "react";

import {
  clearAutoRedirected,
  getSession,
  hasAutoRedirected,
  loginUrl,
  markAutoRedirected,
  type SessionState,
} from "@/lib/session";

import { SignIn } from "./SignIn";

/**
 * Nothing renders until the session is known.
 *
 * The API returns 401 for every collection route when signed out, and the surfaces below swallow
 * those failures by design (Read has to stay usable when the *hub* is down). That combination is
 * exactly wrong for being signed out: you would get the whole app, silently doing nothing, with no
 * hint that a sign-in was needed. So the check happens once, above everything.
 */
export function AuthGate({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<SessionState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const checked = useRef(false);

  useEffect(() => {
    // StrictMode double-invokes mount effects; without this guard the redirect fires twice.
    if (checked.current) return;
    checked.current = true;

    void getSession()
      .then((state) => {
        if (state.authenticated || !state.required) {
          // A completed sign-in clears the loop guard, so a later expiry bounces automatically
          // again rather than landing straight on the manual screen.
          clearAutoRedirected();
          setSession(state);
          return;
        }
        if (!hasAutoRedirected()) {
          markAutoRedirected();
          window.location.replace(loginUrl());
          return;
        }
        setSession(state);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  if (error) {
    return (
      <SignIn reason={`Could not reach the server to check your sign-in — ${error}`} />
    );
  }

  if (!session) {
    // Blank rather than a spinner: this resolves in one request, and on the signed-out path the
    // browser is already leaving for Google.
    return <div className="h-full bg-void" />;
  }

  if (session.required && !session.authenticated) {
    return (
      <SignIn reason="Signing in did not stick. Check that this browser accepts cookies, then try again." />
    );
  }

  return <>{children}</>;
}
